import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Paperclip, Send, X, Loader2, AtSign } from "lucide-react";

export default function ChatComposer({ members = [], onSend, disabled, placeholder, compact }) {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null);
  const taRef = useRef(null);
  const fileRef = useRef(null);

  const detectMention = (value, caret) => {
    const upToCaret = value.slice(0, caret);
    const m = upToCaret.match(/@([^@\n]{0,30})$/);
    setMentionQuery(m ? m[1].toLowerCase() : null);
  };

  const onChange = (e) => {
    setText(e.target.value);
    detectMention(e.target.value, e.target.selectionStart);
  };

  const filteredMembers = mentionQuery === null ? []
    : members.filter((m) => (m.name || "").toLowerCase().includes(mentionQuery)).slice(0, 6);

  const pickMention = (member) => {
    const ta = taRef.current;
    const caret = ta ? ta.selectionStart : text.length;
    const before = text.slice(0, caret).replace(/@([^@\n]{0,30})$/, `@${member.name} `);
    const after = text.slice(caret);
    setText(before + after);
    setMentionQuery(null);
    setTimeout(() => {
      if (ta) {
        ta.focus();
        ta.setSelectionRange(before.length, before.length);
      }
    }, 0);
  };

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((a) => [...a, r.data]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi du fichier");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const send = () => {
    const body = text.trim();
    if (!body && attachments.length === 0) return;
    const mentions = members.filter((m) => body.includes(`@${m.name}`)).map((m) => m.id);
    onSend({ body, mentions, attachments });
    setText("");
    setAttachments([]);
    setMentionQuery(null);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") { setMentionQuery(null); return; }
    if (e.key === "Enter" && !e.shiftKey) {
      if (filteredMembers.length > 0) return; // laisse choisir une mention
      e.preventDefault();
      send();
    }
  };

  return (
    <div className={`border-t border-slate-200 bg-white relative ${compact ? "p-2" : "p-3"}`} data-testid="chat-composer">
      {filteredMembers.length > 0 && (
        <div className="absolute bottom-full left-3 mb-1 w-64 bg-white border border-slate-200 rounded-sm shadow-lg overflow-hidden z-10" data-testid="mention-dropdown">
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1 border-b border-slate-100">
            <AtSign className="w-3 h-3" /> Mentionner
          </div>
          {filteredMembers.map((m) => (
            <button key={m.id} onClick={() => pickMention(m)} data-testid={`mention-option-${m.id}`}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm text-slate-700 flex items-center justify-between">
              <span>{m.name}</span>
              <span className="text-[10px] text-slate-400">{m.role}</span>
            </button>
          ))}
        </div>
      )}

      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {attachments.map((a) => (
            <div key={a.file_id} className="flex items-center gap-1.5 bg-slate-100 rounded-sm pl-2 pr-1 py-1 text-xs text-slate-700">
              <Paperclip className="w-3 h-3" />
              <span className="max-w-[140px] truncate">{a.filename}</span>
              <button onClick={() => setAttachments((list) => list.filter((x) => x.file_id !== a.file_id))} className="text-slate-400 hover:text-red-500">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="chat-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading || disabled} data-testid="chat-attach-btn"
          className="shrink-0 w-10 h-10 rounded-sm border border-slate-300 flex items-center justify-center text-slate-600 hover:border-[#B8873A] hover:text-[#B8873A] transition-colors disabled:opacity-50">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
        </button>
        <textarea
          ref={taRef}
          value={text}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder || "Écris un message… (@ pour mentionner)"}
          rows={1}
          disabled={disabled}
          data-testid="chat-message-input"
          className="flex-1 resize-none px-3 py-2.5 border border-slate-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#111111] focus:border-[#111111] text-sm max-h-32"
        />
        <button onClick={send} disabled={disabled || (!text.trim() && attachments.length === 0)} data-testid="chat-send-btn"
          className="shrink-0 w-10 h-10 rounded-sm bg-[#111111] text-white flex items-center justify-center hover:bg-slate-800 transition-colors disabled:opacity-40">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
